import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import { ThemePalette } from 'mui.theme';
import Chart from './chart';
import { PNG } from 'components/Asset/PNG';
import { StyledTableHeaderCell } from 'views/TirePressure/Components/NotificationTable.styles';
import { isNegative } from 'utils/fuelAnalytics';
import type { productionAsset } from 'types/production.types';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { getShiftHours } from 'views/Utilization/utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { getProductionValuesFromData } from './utils';
import RecommendationsList from './RecommendationsList';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  data: any;
  chartData: productionAsset[];
  customerCode: string;
  unitSystem: string;
  suggestions: any;
}

export const ProductionWidget = ({
  enableDarkTheme,
  data,
  chartData,
  customerCode,
  suggestions,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  type KeyToDisplay = (typeof keyMappings)[keyof typeof keyMappings];
  const [showTableData, setShowTableData] = useState<boolean>(false);

  const [typeGrouping, setTypeGrouping] =
    useState<keyof typeof keyMappings>('Tonnage Moved');

  const fontColor = palette.textPrimary;

  const getProductionValues = useCallback(
    (data: any) => {
      return getProductionValuesFromData(data, typeGrouping);
    },
    [typeGrouping]
  );

  const { productionValue, productionValueDiff } = getProductionValues(data);
  const productionUnit = typeGrouping === 'Cycle Time' ? 'Minutes' : 'Tons';

  const diffIsNegative = isNegative(Number(productionValueDiff));

  const handleChange = (event: any) => {
    setTypeGrouping(event.target.value);
  };
  const keyMappings = {
    'Tonnage Per Hour': 'avg_load_per_hour',
    'Tonnage Moved': 'max_load_sum',
    'Cycle Time': 'avg_cycle_time',
  } as const;

  const keyToDisplay: KeyToDisplay = useMemo(() => {
    return keyMappings[typeGrouping];
  }, [typeGrouping]);

  // const performanceSuggestions = checkAssetPerformance(customerCode, chartData);

  const radioOptions = ['Tonnage Moved', 'Tonnage Per Hour', 'Cycle Time'];

  return (
    <Box
      sx={{
        border: '1px solid #2F445E',
        borderRadius: '4px',
        height: showTableData ? '600px' : '220px',
        overflow: 'scroll',
        color: fontColor,
        padding: '20px',
      }}
    >
      <Box
        sx={{
          height: '220px',
          color: palette.textPrimary,
        }}
      >
        <Grid container spacing={2}>
          <Grid
            item
            lg={3}
            sx={{
              height: '100%',
            }}
          >
            <Box
              sx={{
                padding: '10px',
                border: '1px solid #2F445E',
                height: '200px',
                borderRadius: '4px',
                backgroundColor: palette.paperBackground,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={typeGrouping}
                    onChange={handleChange}
                    sx={{ fontSize: '14px' }}
                  >
                    {radioOptions.map((option) => (
                      <FormControlLabel
                        key={option}
                        value={option}
                        control={
                          <Radio
                            sx={{
                              fontSize: '12px',
                              color: enableDarkTheme
                                ? palette.primary
                                : ThemePalette.typography.primary,
                              '&.Mui-checked': {
                                color: enableDarkTheme
                                  ? palette.primary
                                  : ThemePalette.typography.primary,
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: 14,
                              },
                            }}
                          />
                        }
                        label={option}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                            color: ThemePalette.typography.grey,
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>

              <Typography
                sx={{
                  fontSize: '35px',
                  fontWeight: '700',
                }}
              >
                {productionValue}
                <span
                  style={{
                    marginLeft: '3px',
                    fontSize: '14px',
                    color: ThemePalette.typography.grey,
                  }}
                >
                  {productionUnit}
                </span>
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  color: ThemePalette.typography.grey,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: diffIsNegative ? '#EB5757' : '#009161',
                    marginRight: '4px',
                  }}
                >
                  {diffIsNegative ? (
                    <CallReceivedIcon
                      sx={{
                        fontSize: '14px',
                        marginRight: '3px',
                        color: ThemePalette.typography.lightDanger,
                      }}
                    />
                  ) : (
                    <CallMadeIcon
                      sx={{
                        fontSize: '14px',
                        marginRight: '3px',
                        color: ThemePalette.typography.lightSuccess,
                      }}
                    />
                  )}
                  {truncateTo2dp(Math.abs(productionValueDiff))}%{' '}
                  {diffIsNegative ? 'less' : 'more'}
                </Box>
                {t('from_last_period')}
              </Box>

              <Button
                sx={{
                  textTransform: 'none',
                  border: '1px solid #2F445E',
                  borderRadius: '4px',
                  marginTop: '35px',
                  fontSize: '12px',
                  color: ThemePalette.typography.white,
                }}
                onClick={() => {
                  setShowTableData(!showTableData);
                }}
              >
                {showTableData ? 'Hide Tabular Data' : 'Show Tabular Data'}
                {showTableData ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{
                      marginLeft: '4px',
                    }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      marginLeft: '4px',
                    }}
                  />
                )}
              </Button>
            </Box>
          </Grid>
          <Grid
            item
            lg={5}
            sx={{
              height: '100%',
            }}
          >
            <Box
              sx={{
                padding: '10px',
                border: '1px solid #2F445E',
                height: '200px',
                borderRadius: '4px',
                backgroundColor: palette.paperBackground,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: ThemePalette.typography.grey,
                }}
              >
                {t('by_assets')}
              </Typography>
              <Box
                sx={{
                  height: '180px',
                  maxHeight: '180px',
                }}
              >
                {chartData.length === 0 ? (
                  <Typography
                    sx={{
                      color: fontColor,
                      fontSize: '12px',
                      marginTop: '10px',
                    }}
                  >
                    {' '}
                    {t('no_assets_data')}
                  </Typography>
                ) : (
                  <Chart
                    enableDarkTheme={enableDarkTheme}
                    data={chartData}
                    chartKey={keyToDisplay}
                    keyDisplay={typeGrouping}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item lg={4} sx={{ height: '100%' }}>
            <Box
              sx={{
                padding: '10px',
                overflow: 'scroll',
                border: '1px solid #2F445E',
                height: '200px',
                borderRadius: '4px',
                backgroundColor: palette.paperBackground,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: ThemePalette.typography.grey,
                }}
              >
                {t('suggestions_actions')}
              </Typography>
              <RecommendationsList
                data={suggestions}
                enableDarkTheme={enableDarkTheme}
                showIndexes={[2]}
              />
              {/* <Box>
                <Typography
                  sx={{
                    marginTop: '10px',
                    fontSize: '12px',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {performanceSuggestions}
                </Typography>
              </Box> */}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {showTableData && (
        <Box
          sx={{
            border: '1px solid #2F445E',
            marginTop: '40px',
            padding: '10px',
            borderRadius: '4px',
            backgroundColor: palette.paperBackground,
          }}
        >
          {chartData.length === 0 ? (
            <Typography
              sx={{
                color: fontColor,
                fontSize: '12px',
                marginTop: '10px',
              }}
            >
              {' '}
              {t('no_assets_data')}
            </Typography>
          ) : (
            <TableContainer
              sx={{
                border: '1px solid #2F445E',
                borderRadius: '4px',
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('asset_details')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('tonnage_moved')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('shift_hours')}
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell enabledarktheme={enableDarkTheme}>
                      {t('tonnage_per_hour')}
                    </StyledTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((row: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{
                        '& td': {
                          border: 'none',
                          borderBottom:
                            index !== chartData.length - 1
                              ? '1px solid #2F445E'
                              : 'none',
                        },
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: palette.toolBarBackground,
                              borderRadius: '83px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PNG
                              name={getImageNameByMakeModelAndAsset(
                                row.make,
                                row.model,
                                row.asset_type,
                                'Small'
                              )}
                              alt={t('assetimage')}
                              style={{
                                height: '35px',
                                width: '100%',
                              }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: fontColor,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'left',
                                }}
                              >
                                {row.bumper_id}
                              </Box>
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', color: fontColor }}
                            >
                              {`${row.make}-${row.model}`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {truncateTo2dp(row[keyToDisplay])} Tons
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {getShiftHours(customerCode)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: fontColor,
                          }}
                        >
                          {truncateTo2dp(row.avg_load_per_hour)} Tons
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};
